import { type Transition, transitionGuard } from './machine';

/**
 * Consultation session (PRD §6). Phase 1 uses provisional, confirmed, completed and
 * canceled. `in_progress` and `ended` are driven by Phase 2's in-platform sessions.
 */
export const CONSULTATION_STATES = [
  'provisional',
  'confirmed',
  'in_progress',
  'ended',
  'completed',
  'canceled',
] as const;
export type ConsultationState = (typeof CONSULTATION_STATES)[number];

export type ConsultationEvent = 'confirm' | 'reschedule' | 'complete' | 'cancel';

const TRANSITIONS: readonly Transition<ConsultationState, ConsultationEvent>[] = [
  { from: ['provisional'], on: 'confirm', to: 'confirmed' },
  { from: ['provisional', 'confirmed'], on: 'reschedule', to: 'confirmed' },
  { from: ['confirmed', 'ended'], on: 'complete', to: 'completed' },
  { from: ['provisional', 'confirmed'], on: 'cancel', to: 'canceled' },
];

export const consultationMachine = transitionGuard<ConsultationState, ConsultationEvent>(
  'consultation',
  TRANSITIONS,
);

/**
 * Where the session runs. `zoom`: Concord creates the Zoom meeting and sends its link.
 * `external`: a navigator pasted the link. `realtimekit` was the original Phase 2 plan.
 */
export const CONSULTATION_PROVIDERS = ['external', 'realtimekit', 'zoom'] as const;
export type ConsultationProvider = (typeof CONSULTATION_PROVIDERS)[number];

export type JoinWindow =
  | { status: 'not_yet'; opensAt: Date; minutesUntilOpen: number }
  | { status: 'open' }
  | { status: 'over' };

/** SCH-09: the join action opens `windowMinutes` before the start and closes when the session should be over. */
export function joinWindow(
  start: Date,
  durationMinutes: number,
  windowMinutes: number,
  now: Date,
): JoinWindow {
  const opensAt = new Date(start.getTime() - windowMinutes * 60_000);
  const closesAt = new Date(start.getTime() + (durationMinutes + 60) * 60_000);
  if (now < opensAt) {
    return {
      status: 'not_yet',
      opensAt,
      minutesUntilOpen: Math.ceil((opensAt.getTime() - now.getTime()) / 60_000),
    };
  }
  if (now > closesAt) return { status: 'over' };
  return { status: 'open' };
}
